'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import clsx from 'clsx'

import { useFocusTrap } from 'shared/ui/hooks/useFocusTrap'
import { ClientOnlyPortal } from 'shared/ui/primitives/ClientOnlyPortal'
import { KEYBOARD_KEYS } from 'shared/ui/types'
import { isNilOrEmpty } from 'shared/utils/ramda'

import SelectList from './SelectList'
import { normalizeToArray } from './utils'

import styles from './SelectDropdown.module.scss'

// Default values
export const DEFAULT_SIZE = 'medium' as const
export const DEFAULT_VARIANT = 'primary' as const

type SelectOptionType = {
  label: string | React.ReactNode
  value: string
}

interface SelectProps {
  variant?: 'primary' | 'ghost'
  options: SelectOptionType[]
  name: string
  label?: string
  value: string | string[]
  required?: boolean
  disabled?: boolean
  multiple?: boolean
  onChange?: (name: string, value: string | string[]) => void
  noMargin?: boolean
  size?: 'medium' | 'large'
  id: string
  errorMessage?: string
}

const SelectDropdown = ({
  options,
  name,
  label,
  value = '',
  required,
  disabled = false,
  multiple = false,
  onChange,
  size = DEFAULT_SIZE,
  variant = 'primary',
  noMargin,
  id,
  errorMessage,
  ...rest
}: SelectProps) => {
  const [touched, setTouched] = useState(false)
  const [error, setError] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const labelId = label ? `${id}-label` : undefined
  const listboxId = `${id}-listbox`
  const selectRef = useRef<HTMLButtonElement>(null)
  const listboxRef = useRef<HTMLUListElement | null>(null)
  const listboxWrapperRef = useRef<HTMLDivElement>(null)
  const formControlRef = useRef<HTMLDivElement>(null)

  useFocusTrap({
    container: listboxRef,
    active: isOpen,
    initialFocusAnchor: selectRef.current ?? null,
    previouslyFocusedElement: selectRef
  })

  const selectedValues = useMemo(
    () => normalizeToArray(value, multiple),
    [value, multiple]
  )

  const closeSelect = useCallback(() => {
    setIsOpen(false)
    selectRef.current?.focus()
  }, [])

  const handleBlur = useCallback((e: React.FocusEvent) => {
    if (!listboxRef.current?.contains(e.relatedTarget)) {
      setTouched(true)
    }
  }, [])

  const handleOpen = useCallback(() => {
    setIsOpen(prev => !prev)
    setTimeout(() => {
      listboxRef.current?.focus()
    }, 100)
  }, [])

  useEffect(() => {
    if (isNilOrEmpty(value) && touched && required) {
      setError(true)
    } else {
      setError(false)
    }
  }, [value, touched, required])

  const handleSelect = useCallback(
    (selectedValue: string) => {
      if (disabled) return
      let newValue: string | string[]

      if (multiple) {
        const isSelected = selectedValues.includes(selectedValue)

        newValue = isSelected
          ? selectedValues.filter(v => v !== selectedValue)
          : [...selectedValues, selectedValue]
      } else {
        newValue = selectedValue
        setIsOpen(false)
        selectRef.current?.focus()
      }

      onChange?.(name, newValue)
    },
    [disabled, multiple, selectedValues, onChange, name]
  )

  useEffect(() => {
    if (!isOpen) return

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node

      if (
        selectRef.current &&
        !selectRef.current.contains(target) &&
        listboxRef &&
        !listboxRef.current?.contains(target)
      ) {
        closeSelect()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, closeSelect])

  // Convert selected option labels to display string
  const displayValue = useMemo(() => {
    if (multiple) {
      const selectedLabels = options
        .filter(opt => selectedValues.includes(opt.value))
        .map(opt => {
          if (typeof opt.label === 'string') return opt.label
          if (typeof opt.label === 'number') return String(opt.label)

          return ''
        })
        .filter(Boolean)

      return selectedLabels.length > 0 ? selectedLabels.join(', ') : ''
    }

    const selectedOption = options.find(opt => opt.value === value)

    if (!selectedOption) return ''
    if (typeof selectedOption.label === 'string') return selectedOption.label
    if (typeof selectedOption.label === 'number')
      return String(selectedOption.label)

    return ''
  }, [multiple, value, options, selectedValues])

  // Too cheap to memoize - simple boolean check
  const hasValue = multiple ? selectedValues.length > 0 : !isNilOrEmpty(value)

  const shouldShrinkLabel = isOpen || hasValue

  const ariaLabel = useMemo(() => {
    const dropdownText = multiple
      ? 'wielokrotny wybór, lista rozwijana'
      : 'lista rozwijana'

    const baseLabel = label
      ? `${label}, ${dropdownText}`
      : dropdownText.charAt(0).toUpperCase() + dropdownText.slice(1)

    // Include selected value in aria-label for screen readers
    if (hasValue) {
      const valueText = multiple
        ? `${selectedValues.length} ${selectedValues.length === 1 ? 'opcja wybrana' : 'opcje wybrane'}: ${displayValue}`
        : displayValue

      return `${baseLabel}, wybrano: ${valueText}`
    }

    return baseLabel
  }, [label, multiple, hasValue, selectedValues, displayValue])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === KEYBOARD_KEYS.ESCAPE) {
        e.preventDefault()
        e.stopPropagation()
        closeSelect()
        selectRef.current?.click()
      }
    },
    [closeSelect]
  )

  const isLarge = size !== 'medium'

  return (
    <div
      {...rest}
      className={clsx(
        styles.wrapper,
        {
          [styles.wrapperLarge]: isLarge,
          [styles.wrapperNoMargin]: noMargin
        },
        (rest as { className?: string }).className
      )}
      onKeyDown={handleKeyDown}
    >
      <div ref={formControlRef} className={styles.formControl}>
        {label && (
          <label
            id={labelId}
            htmlFor={id}
            className={clsx(styles.label, {
              [styles.labelLarge]: isLarge,
              [styles.labelShrink]: shouldShrinkLabel,
              [styles.labelError]: error,
              [styles.labelDisabled]: !error && disabled
            })}
          >
            {label}
            {required && (
              <span aria-hidden='true' className={styles.requiredStar}>
                {' *'}
              </span>
            )}
          </label>
        )}
        <button
          ref={selectRef}
          id={id}
          type='button'
          role='combobox'
          aria-haspopup='listbox'
          aria-expanded={isOpen}
          aria-label={ariaLabel}
          aria-labelledby={labelId}
          aria-controls={isOpen ? listboxId : undefined}
          aria-required={required}
          aria-invalid={error}
          className={clsx(styles.button, {
            [styles.buttonLarge]: isLarge,
            [styles.buttonLabelShrink]: Boolean(label) && shouldShrinkLabel,
            [styles.buttonDisabled]: disabled,
            [styles.buttonGhost]: variant === 'ghost',
            [styles.buttonError]: variant !== 'ghost' && error,
            [styles.buttonPrimary]: variant !== 'ghost' && !error
          })}
          onClick={handleOpen}
          onBlur={handleBlur}
          disabled={disabled}
        >
          <span
            className={clsx(styles.value, {
              [styles.valueFilled]: hasValue && !disabled,
              [styles.valueFilledDisabled]: hasValue && disabled
            })}
          >
            {displayValue || (multiple ? '' : 'Wybierz opcję')}
          </span>
          <span
            aria-hidden='true'
            className={clsx(styles.icon, { [styles.iconOpen]: isOpen })}
          >
            ▼
          </span>
        </button>
        <ClientOnlyPortal selector='#modal-portal'>
          <SelectList
            ref={listboxRef}
            wrapperRef={listboxWrapperRef}
            onSelect={handleSelect}
            onClose={closeSelect}
            isOpen={isOpen}
            selectedValues={selectedValues}
            options={options}
            anchorRef={selectRef}
            multiple={multiple}
            size={size}
            variant={variant}
            id={listboxId}
            aria-labelledby={labelId}
          />
          ,
        </ClientOnlyPortal>
      </div>
      {(errorMessage || error) && (
        <div role='alert' id={`${id}-error`} className={styles.error}>
          Proszę wybrać jedną z opcji
        </div>
      )}
    </div>
  )
}

export default SelectDropdown
